import { useEffect, useMemo, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Download, Trash2, CheckSquare, Square, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function NewsletterAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/newsletter"); setItems(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const s = search.toLowerCase();
    return items.filter((i) => (i.email || "").toLowerCase().includes(s) || (i.first_name || "").toLowerCase().includes(s));
  }, [items, search]);

  const allSelected = filtered.length > 0 && filtered.every((m) => selected.has(m.id));
  const toggleAll = () => {
    const s = new Set(selected);
    if (allSelected) filtered.forEach((m) => s.delete(m.id));
    else filtered.forEach((m) => s.add(m.id));
    setSelected(s);
  };
  const toggleOne = (id) => {
    const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("fic_token");
      const r = await fetch(`${API_BASE}/admin/newsletter/export`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("Export failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "fic_newsletter.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const deleteOne = async (id) => {
    if (!window.confirm("Remove this subscriber?")) return;
    try {
      await api.delete(`/admin/newsletter/${id}`);
      setItems((p) => p.filter((i) => i.id !== id));
      toast.success("Removed");
    } catch { toast.error("Failed"); }
  };

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Remove ${selected.size} subscriber${selected.size === 1 ? "" : "s"}?`)) return;
    setDeleting(true);
    let failed = 0;
    await Promise.all([...selected].map(async (id) => {
      try { await api.delete(`/admin/newsletter/${id}`); } catch { failed++; }
    }));
    setItems((p) => p.filter((i) => !selected.has(i.id)));
    setSelected(new Set());
    setDeleting(false);
    toast.success(`Removed ${selected.size - failed}${failed ? ` · ${failed} failed` : ""}`);
  };

  return (
    <div data-testid="admin-newsletter-page">
      <div className="flex justify-between items-start mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Manage</div>
          <h1 className="font-serif text-4xl mt-2 text-ink">Newsletter subscribers</h1>
          <p className="mt-2 text-ink/60">{items.length} total</p>
        </div>
        <button onClick={exportCsv} data-testid="export-csv-btn"
                className="inline-flex items-center gap-2 rounded-full bg-gold text-emerald_deep px-5 py-2.5 text-sm font-semibold hover:bg-gold-600">
          <Download size={14} /> Export CSV
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-4 bg-white border border-sand rounded-2xl p-3">
        <button
          type="button" onClick={toggleAll} data-testid="ns-toggle-all"
          disabled={filtered.length === 0}
          className="inline-flex items-center gap-2 text-sm text-ink/75 hover:text-navy px-3 py-1.5 rounded-lg hover:bg-sand/40 disabled:opacity-40"
        >
          {allSelected ? <CheckSquare size={14} /> : <Square size={14} />} {allSelected ? "Unselect all" : "Select all"}
        </button>
        <div className="inline-flex items-center gap-2 flex-1 max-w-sm">
          <Search size={14} className="text-ink/50" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or email…"
            data-testid="ns-search"
            className="flex-1 px-3 py-1.5 rounded-lg border border-sand focus:outline-none focus:border-navy text-sm"
          />
        </div>
        <div className="flex-1" />
        {selected.size > 0 && (
          <button
            type="button" onClick={deleteSelected} disabled={deleting}
            data-testid="ns-delete-selected"
            className="inline-flex items-center gap-2 rounded-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider disabled:opacity-60"
          >
            {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
            Remove {selected.size} selected
          </button>
        )}
      </div>

      {loading ? <div className="text-ink/40">Loading…</div> :
        filtered.length === 0 ? (
          <div className="rounded-3xl bg-white border border-sand p-14 text-center text-ink/60">
            {items.length === 0 ? "No subscribers yet." : "No subscribers match your search."}
          </div>
        ) : (
          <div className="rounded-3xl bg-white border border-sand admin-scroll admin-scroll-hint">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-sand/50 text-left">
                <tr>
                  <th className="px-3 py-3 w-10"></th>
                  <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-ink/60">First Name</th>
                  <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-ink/60">Email</th>
                  <th className="px-5 py-3 text-[10px] uppercase tracking-wider text-ink/60">Subscribed</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => (
                  <tr key={it.id} data-testid={`ns-row-${it.id}`} className={`border-t border-sand hover:bg-cream/60 ${selected.has(it.id) ? "bg-navy/[0.03]" : ""}`}>
                    <td className="px-3 py-3">
                      <input type="checkbox" checked={selected.has(it.id)} onChange={() => toggleOne(it.id)}
                        data-testid={`ns-check-${it.id}`} className="w-4 h-4 accent-navy" />
                    </td>
                    <td className="px-5 py-3 text-ink">{it.first_name}</td>
                    <td className="px-5 py-3 text-ink">{it.email}</td>
                    <td className="px-5 py-3 text-ink/60">{new Date(it.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => deleteOne(it.id)} className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
